import { FC, useState, useEffect } from "react";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import QRCode from "react-qr-code";

import { Input, Label, Button, Message } from "../../common";
import { FormErrorMessage } from "../../messages";
import {
  useInitTfaMutation,
  useEnableTfaMutation,
} from "../../../store/services/tfa";
import { UIStatus } from "../../../types/GlobalTypes";
import { toastSuccess } from "../../../utils/toasts";

interface IFormInputs {
  code?: string;
}

type Props = {
  closeForm: () => void;
};

export const TfaVerificationForm: FC<Props> = ({ closeForm }) => {
  const [showCode, setShowCode] = useState(false);
  const [url, setUrl] = useState("");
  const [secrectCode, setSecretCode] = useState("");

  const {
    handleSubmit,
    control,
    formState: { isDirty, isValid },
  } = useForm<IFormInputs>({
    defaultValues: {
      code: "",
    },
  });

  const [initTfa] = useInitTfaMutation();

  const [enableTfa, { isSuccess, isLoading, isError, error }] =
    useEnableTfaMutation();

  useEffect(() => {
    const init = async () => {
      const res = await initTfa().unwrap();
      setUrl(res.provisioning_uri);
      setSecretCode(res.secret_key);
    };

    init();
  }, [initTfa]);

  useEffect(() => {
    if (isSuccess) {
      closeForm();
      toastSuccess("Two-factor verification enabled");
    }
  }, [isLoading, isSuccess, closeForm, secrectCode]);

  const onSubmit: SubmitHandler<IFormInputs> = async (data: IFormInputs) =>
    await enableTfa(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="text-center flex flex-col items-center mb-8">
        <div
          className="mb-8 theme-subheading text-gray-500 underline"
          onClick={() => setShowCode(!showCode)}
        >
          {showCode ? "Or scan QR code" : "Or enter setup key manually"}
        </div>

        {showCode
          ? secrectCode && (
              <Message
                type={UIStatus.NEUTRAL}
                text={secrectCode}
                className="text-center w-full"
              />
            )
          : url && <QRCode value={url} />}

        <div className="mt-8 theme-subheading">
          Enter the code generated by your app
        </div>
      </div>

      <Label label="6-digit code" className="mb-12">
        <Controller
          name="code"
          control={control}
          render={({ field }) => (
            <Input
              type="text"
              maxLength="6"
              minLength="6"
              autoFocus
              {...field}
            />
          )}
        />
      </Label>

      <FormErrorMessage className="mb-6" isError={isError} error={error} />

      <Button
        type="submit"
        variant={Button.variant.PRIMARY}
        className="w-full mb-6"
        disabled={!isDirty || !isValid}
      >
        Verify
      </Button>

      <Button
        variant={Button.variant.TERTIARY}
        className="w-full"
        onClick={closeForm}
      >
        Go back
      </Button>
    </form>
  );
};
